import React, { useState } from 'react';
import AtlasV2MetricListContainer, {
  AtlasV2MetricListContainerProps
} from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import {
  KeywordColumnDefinition,
  ProductColumnDefinition,
  getWeekColumnDefinition
} from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { removeComparisonFormatter } from 'src/components/BeaconV2Components/MetricList/utils';
import ProductGrid from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import { useMainEntityType, useProductEntity } from 'src/utils/Hooks/reduxSelectors';

export enum TrafficDropdownOption {
  Keyword = 'searchTerm',
  Product = 'stacklineSku',
  Week = 'weekId'
}

const configByEntityType = {
  product: {
    [TrafficDropdownOption.Keyword]: {
      headerDisplayName: 'Keyword',
      headerColumnDefinition: KeywordColumnDefinition,
      fields: [
        {
          name: 'organicClicks',
          displayName: 'Organic Traffic'
        },
        {
          name: 'adClicks',
          displayName: 'Paid Traffic'
        },
        {
          name: 'totalClicks',
          displayName: 'Total Traffic'
        },
        {
          name: 'adSpend',
          displayName: 'Paid Ad Spend'
        }
      ],
      appTableProps: {
        sx: {
          'tr td:first-child': {
            width: 316,
            'div.keyword-col': {
              width: '284px !important'
            }
          }
        }
      } as AtlasV2MetricListContainerProps['appTableProps']
    },
    [TrafficDropdownOption.Week]: {
      headerDisplayName: 'Week',
      headerColumnDefinition: getWeekColumnDefinition({
        getTableColumnProps(defaults) {
          return {
            ...defaults,
            style: {
              ...defaults.style,
              width: '220px'
            }
          };
        }
      }),
      fields: [
        {
          name: 'organicClicks',
          displayName: 'Organic Traffic'
        },
        {
          name: 'adClicks',
          displayName: 'Paid Traffic'
        },
        {
          name: 'totalClicks',
          displayName: 'Total Traffic'
        },
        {
          name: 'adSpend',
          displayName: 'Paid Ad Spend'
        }
      ],
      postProcessData: (data) => {
        return data.sort((a, b) => b.weekId.localeCompare(a.weekId));
      },
      appTableProps: {
        sx: {
          'tr td:first-child': {
            width: 292,
            'div.product-col': {
              width: 'unset !important'
            }
          }
        }
      } as AtlasV2MetricListContainerProps['appTableProps']
    }
  },
  default: {
    [TrafficDropdownOption.Keyword]: {
      headerDisplayName: 'Keyword',
      headerColumnDefinition: KeywordColumnDefinition,
      fields: [
        {
          name: 'organicClicks',
          displayName: 'Organic Traffic'
        },
        {
          name: 'adClicks',
          displayName: 'Paid Traffic'
        },
        {
          name: 'totalClicks',
          displayName: 'Total Traffic'
        },
        {
          name: 'adSpend',
          displayName: 'Paid Ad Spend'
        }
      ],
      appTableProps: {
        sx: {
          'tr td:first-child': {
            width: 316,
            'div.keyword-col': {
              width: 'unset !important'
            }
          }
        }
      } as AtlasV2MetricListContainerProps['appTableProps']
    },
    [TrafficDropdownOption.Product]: {
      headerDisplayName: 'Product',
      headerColumnDefinition: ProductColumnDefinition,
      fields: [
        {
          name: 'organicClicks',
          displayName: 'Organic Traffic'
        },
        {
          name: 'adClicks',
          displayName: 'Paid Traffic'
        },
        {
          name: 'otherClicks',
          displayName: 'Other Traffic'
        },
        {
          name: 'totalClicks',
          displayName: 'Total Traffic'
        },
        {
          name: 'adSpend',
          displayName: 'Paid Ad Spend'
        }
      ],
      appTableProps: {
        sx: {
          'tr td:first-child': {
            width: 292,
            'div.product-col': {
              width: 'unset !important'
            }
          }
        }
      } as AtlasV2MetricListContainerProps['appTableProps']
    }
  }
};

const subtabConfig: { [key: string]: { title: string; sortFieldName: string; prefixTitle: string } } = {
  totalClicks: {
    title: 'Total Traffic By Week',
    sortFieldName: 'totalClicks',
    prefixTitle: 'Total Traffic by'
  },
  organicClicks: {
    title: 'Organic Traffic By Week',
    sortFieldName: 'organicClicks',
    prefixTitle: 'Organic Traffic by'
  },
  adClicks: { title: 'Paid Traffic By Week', sortFieldName: 'adClicks', prefixTitle: 'Paid Traffic by' }
};

/**
 * Grid for traffic by keyword and product - this applies to all traffic tabs
 */
const AtlasV2TrafficProductTable = ({ subtab }: { subtab: 'totalClicks' | 'organicClicks' | 'adClicks' }) => {
  const [dropdownOption, setDropdownOption] = useState<TrafficDropdownOption>(TrafficDropdownOption.Keyword);
  const { isProductEntity } = useProductEntity();
  const entityType = useMainEntityType();

  const config = configByEntityType?.[entityType] ?? configByEntityType.default;
  const { headerColumnDefinition, fields, postProcessData, appTableProps } = config[dropdownOption];
  const weekEndingFields = (fields ?? []).map(removeComparisonFormatter);
  const subtabToUse = subtabConfig[subtab] || subtabConfig.totalClicks;

  return isProductEntity ? (
    <AtlasV2MetricListContainer
      entityTableHeaderProps={{
        prefixTitle: subtabToUse.prefixTitle,
        title: '',
        groupByFields: [TrafficDropdownOption.Keyword, TrafficDropdownOption.Week].map((option) => ({
          name: option,
          displayName: config[option].headerDisplayName
        })),
        handleGroupByChange: (event: { target: { value: string } }) => {
          setDropdownOption((prevOption) => {
            const newValue = event.target.value as TrafficDropdownOption;
            if (prevOption !== newValue) {
              return newValue;
            }
            return prevOption;
          });
        }
      }}
      indexName="traffic"
      sortFieldName={subtabToUse.sortFieldName}
      fields={weekEndingFields}
      headColumnDefinition={headerColumnDefinition}
      postProcessData={postProcessData}
      useTableDataProps={{
        groupByFieldName: dropdownOption,
        filterResponse: () => true,
        itemsPerPage: 100
      }}
      showGrid={false}
      appTableProps={appTableProps}
    />
  ) : (
    <AtlasV2MetricListContainer
      skeletonRows={20}
      showGrid={dropdownOption === TrafficDropdownOption.Product}
      renderGrid={(args) => {
        return <ProductGrid {...args} />;
      }}
      headColumnDefinition={headerColumnDefinition}
      entityTableHeaderProps={{
        prefixTitle: subtabToUse.prefixTitle,
        title: '',
        groupByFields: [TrafficDropdownOption.Keyword, TrafficDropdownOption.Product].map((option) => ({
          name: option,
          displayName: config[option].headerDisplayName
        })),
        handleGroupByChange: (event: { target: { value: string } }) => {
          setDropdownOption((prevOption) => {
            const newValue = event.target.value as TrafficDropdownOption;
            if (prevOption !== newValue) {
              return newValue;
            }
            return prevOption;
          });
        }
      }}
      indexName="traffic"
      fields={fields}
      useTableDataProps={{
        groupByFieldName: dropdownOption,
        filterResponse: () => true
      }}
      sortFieldName={subtabToUse.sortFieldName}
      iconList={[IconsList.export, IconsList.tile, IconsList.table]}
      appTableProps={appTableProps}
    />
  );
};

export default AtlasV2TrafficProductTable;
